/**
 * AITourGuideLauncher — Friendly AI tour finder for the dashboard.
 *
 * Behavior:
 *   • Collapsed by default: a small pill at bottom-right ("🧭 Need a quick tour?")
 *   • Expand: a chat-style panel with a single input
 *   • User types ("set up my website", "automate my bills") → AI returns the
 *     best-fit tour ID + a friendly intro line
 *   • Click "Start tour" → navigates to /guided-tours?start=<id> which
 *     auto-launches the walkthrough
 *   • Dismiss (X) → permanently hide via localStorage (with an "undo" toast)
 *   • Minimize (–) → collapse back to pill (NOT permanent)
 *
 * Mounted only on the Dashboard so it never collides with other floating
 * widgets app-wide.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../fetchInterceptor';
import { Compass, X, Minus, Send, Loader2, Sparkles, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISSED_KEY = 'aiTourGuide.dismissed';

// Quick prompts users can click instead of typing
const QUICK_PROMPTS = [
  'Set up my public website',
  'Categorize my bank transactions',
  'Map accounts to my tax form',
  'Invite a client to the portal',
  'Send my first invoice',
];

export default function AITourGuideLauncher() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; }
  });
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  // First-time gentle nudge — auto-open the panel after 4s on dashboard, only once per session
  useEffect(() => {
    if (dismissed) return;
    let seen = false;
    try { seen = sessionStorage.getItem('aiTourGuide.openedThisSession') === '1'; } catch { /* noop */ }
    if (seen) return;
    const t = setTimeout(() => {
      setExpanded(true);
      try { sessionStorage.setItem('aiTourGuide.openedThisSession', '1'); } catch { /* noop */ }
    }, 4000);
    return () => clearTimeout(t);
  }, [dismissed]);

  const handleAsk = async (text) => {
    const q = (text ?? query).trim();
    if (!q || loading) return;
    setLoading(true);
    setSuggestion(null);
    try {
      const r = await apiFetch(`${API_URL}/api/tours/recommend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q }),
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j.detail || 'Could not find a tour for that');
        return;
      }
      setSuggestion(j);
    } catch {
      toast.error('Network hiccup — try again');
    } finally {
      setLoading(false);
    }
  };

  const startTour = () => {
    if (!suggestion) return;
    setExpanded(false);
    setSuggestion(null);
    setQuery('');
    navigate(`/guided-tours?start=${suggestion.tour_id}`);
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
    setExpanded(false);
    // Offer one-click undo
    toast.success('Tour Guide hidden. You can bring it back from Settings → Tour Guide.', {
      action: {
        label: 'Undo',
        onClick: () => {
          try { localStorage.removeItem(DISMISSED_KEY); } catch { /* noop */ }
          setDismissed(false);
        },
      },
      duration: 6000,
    });
  };

  // Permanently hidden — render nothing.
  if (dismissed) return null;

  // Collapsed pill
  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="fixed bottom-5 right-5 z-40 group flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white text-sm font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
        data-testid="ai-tour-guide-launcher"
        aria-label="Open AI Tour Guide"
      >
        <Compass className="w-4 h-4 transition-transform group-hover:rotate-12" />
        <span>Need a quick tour?</span>
      </button>
    );
  }

  // Expanded panel
  return (
    <div
      className="fixed bottom-5 right-5 z-40 w-[340px] max-w-[calc(100vw-2rem)] bg-background border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200"
      data-testid="ai-tour-guide-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-violet-600 to-blue-600 text-white flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-white/15 backdrop-blur-sm flex items-center justify-center">
            <Compass className="w-4 h-4" />
          </div>
          <div>
            <p className="font-semibold text-sm leading-tight">AI Tour Guide</p>
            <p className="text-[10px] text-white/80 leading-tight">Tell me what you want to do</p>
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="p-1.5 hover:bg-white/15 rounded-md"
            data-testid="ai-tour-guide-minimize"
            aria-label="Minimize"
            title="Minimize"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="p-1.5 hover:bg-white/15 rounded-md"
            data-testid="ai-tour-guide-dismiss"
            aria-label="Don't show again"
            title="Don't show again"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-3 space-y-2.5 max-h-[420px] overflow-y-auto">
        {!suggestion && !loading && (
          <>
            <div className="flex items-stretch gap-1.5">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAsk(); }}
                placeholder="e.g. set up my website"
                className="flex-1 h-9 px-3 text-sm rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="ai-tour-guide-input"
                autoFocus
              />
              <button
                type="button"
                onClick={() => handleAsk()}
                disabled={!query.trim() || loading}
                className="h-9 w-9 flex items-center justify-center rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white"
                data-testid="ai-tour-guide-send"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5 mt-2">
                Or pick one
              </p>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => { setQuery(p); handleAsk(p); }}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-dashed border-border bg-muted/40 hover:bg-accent hover:border-solid text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`ai-tour-guide-quick-${p.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {loading && (
          <div className="py-6 flex flex-col items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
            <p className="text-xs text-muted-foreground">Finding the right tour…</p>
          </div>
        )}

        {suggestion && !loading && (
          <div className="space-y-3" data-testid="ai-tour-guide-suggestion">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
              <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm leading-snug text-violet-900 dark:text-violet-100">{suggestion.intro}</p>
                <p className="text-[11px] mt-1.5 text-violet-700 dark:text-violet-300">
                  Tour: <strong>{suggestion.tour_title}</strong>
                  {suggestion.confidence < 60 && <span className="ml-1 text-amber-700 dark:text-amber-300">· best guess</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={startTour}
                className="flex-1 h-9 rounded-lg bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white text-sm font-semibold shadow"
                data-testid="ai-tour-guide-start"
              >
                Start tour →
              </button>
              <button
                type="button"
                onClick={() => { setSuggestion(null); setQuery(''); }}
                className="h-9 px-3 rounded-lg border text-xs hover:bg-accent inline-flex items-center gap-1"
                data-testid="ai-tour-guide-reset"
                title="Ask something else"
              >
                <RotateCcw className="w-3 h-3" /> Ask again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
